import { useEffect, useRef } from 'react'

import { sendGTMEvent } from '@b-stock/fe-core/bstock-next/components'
import type { AllListingsListing } from '@b-stock/search-api-client'

import { GTM_PLATFORM } from './constants'
import { allListingsToGTMItemInput } from './gtmItemAdapters'
import { transformListingToGTMItem } from './transformListingToGTMItem'

interface UseViewItemListOptions {
  listings: AllListingsListing[]
  itemListId: string
  itemListName: string
  currency?: string
  maxItems?: number
  enabled?: boolean
  isNewSeller?: (listing: AllListingsListing) => boolean
}

/**
 * Custom hook to track GTM view_item_list events for auction listings.
 * Fires when users view a list of items, including on initial load, pagination, and filter changes.
 *
 * @example
 * ```tsx
 * useViewItemList({
 *   listings: data?.listings || [],
 *   itemListId: 'all-auctions-grid',
 *   itemListName: 'All Auctions - Grid View',
 *   enabled: !isLoading && !error && (data?.listings.length ?? 0) > 0,
 * })
 * ```
 */
export function useViewItemList({
  listings,
  itemListId,
  itemListName,
  currency = 'USD',
  maxItems = 50,
  enabled = true,
  isNewSeller,
}: UseViewItemListOptions) {
  //Used to ensure we only fire when listingIds change in the item_list.
  const prevListingsKeyRef = useRef('')

  useEffect(() => {
    // Don't fire if disabled or no listings available
    if (!enabled || !listings.length) {
      return
    }

    const listingsKey = listings
      .slice(0, maxItems)
      .map((l) => l.listingId || l.id)
      .join(',')

    if (listingsKey === prevListingsKeyRef.current) {
      return
    }
    prevListingsKeyRef.current = listingsKey

    const itemsToTrack = listings.slice(0, maxItems)

    const items = itemsToTrack
      .filter((listing) => listing.title)
      .map((listing, index) => ({
        ...transformListingToGTMItem(
          allListingsToGTMItemInput(listing),
          index + 1,
          itemListId,
          itemListName
        ),
        ...(isNewSeller !== undefined && {
          is_new_seller: isNewSeller(listing),
        }),
      }))

    const event = {
      event: 'view_item_list',
      platform: GTM_PLATFORM,
      ecommerce: {
        currency,
        item_list_id: itemListId,
        item_list_name: itemListName,
        items,
      },
    }

    sendGTMEvent({ ecommerce: null })
    sendGTMEvent(event)
  }, [
    listings,
    itemListId,
    itemListName,
    currency,
    maxItems,
    enabled,
    isNewSeller,
  ])
  // Including listings in dependency to ensure we re-fire when pagination / filtering happens.
}
